import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import {
  Plus, Edit3, Trash2, Pause, Play, Loader2, RefreshCw,
  ArrowUpDown, ArrowUp, ArrowDown, Save, X, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (v) => Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

const emptyForm = { supplier_name: '', amount: '', day_of_month: 10, description: '', iban: '', category: 'fornitore', company_id: 'agora' };

export default function RecurringCostsPage() {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genMonth, setGenMonth] = useState(new Date().getMonth() + 1);
  const [genYear, setGenYear] = useState(new Date().getFullYear());
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const fetchCosts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/recurring-costs');
      setCosts(res.data.costs || []);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCosts(); }, []);

  const handleSave = async () => {
    if (!form.supplier_name.trim()) { toast.error('Nome fornitore obbligatorio'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Importo deve essere > 0'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/recurring-costs/${editingId}`, form);
        toast.success('Costo aggiornato');
      } else {
        await api.post('/recurring-costs', form);
        toast.success('Costo creato');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyForm });
      fetchCosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
    setSaving(false);
  };

  const handleEdit = (cost) => {
    setForm({
      supplier_name: cost.supplier_name || '',
      amount: cost.amount || '',
      day_of_month: cost.day_of_month || 10,
      description: cost.description || '',
      iban: cost.iban || '',
      category: cost.category || 'fornitore',
      company_id: cost.company_id || 'agora',
    });
    setEditingId(cost.id);
    setShowForm(true);
  };

  const handleToggleActive = async (cost) => {
    try {
      await api.put(`/recurring-costs/${cost.id}`, { active: !cost.active });
      toast.success(cost.active ? 'Sospeso' : 'Riattivato');
      fetchCosts();
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (cost) => {
    if (!window.confirm(`Eliminare "${cost.supplier_name}"?`)) return;
    try {
      await api.delete(`/recurring-costs/${cost.id}`);
      toast.success('Eliminato');
      fetchCosts();
    } catch { toast.error('Errore'); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/recurring-costs/generate-invoices', { month: genMonth, year: genYear });
      const d = res.data;
      toast.success(d.message || `${d.generated} fatture generate`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
    setGenerating(false);
  };

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 ml-1 text-slate-300" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 text-slate-700" /> : <ArrowDown className="h-3 w-3 ml-1 text-slate-700" />;
  };
  const sorted = [...costs].sort((a, b) => {
    if (!sortCol) return 0;
    let va, vb;
    switch (sortCol) {
      case 'supplier_name': va = (a.supplier_name || '').toLowerCase(); vb = (b.supplier_name || '').toLowerCase(); break;
      case 'amount': return sortDir === 'asc' ? (a.amount || 0) - (b.amount || 0) : (b.amount || 0) - (a.amount || 0);
      case 'day_of_month': return sortDir === 'asc' ? (a.day_of_month || 0) - (b.day_of_month || 0) : (b.day_of_month || 0) - (a.day_of_month || 0);
      case 'active': va = a.active ? 'a' : 'z'; vb = b.active ? 'a' : 'z'; break;
      default: return 0;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalMonthly = costs.filter(c => c.active).reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <Layout title="Costi Fissi Ricorrenti">
      <div className="space-y-4" data-testid="recurring-costs-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-600" /> Costi Fissi Ricorrenti
            </h2>
            <p className="text-sm text-slate-500">Totale mensile attivo: <span className="font-semibold text-slate-700">{fmtEur(totalMonthly)}</span> — {costs.filter(c => c.active).length} costi attivi</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setShowForm(true); setEditingId(null); setForm({ ...emptyForm }); }} data-testid="add-cost-btn">
              <Plus className="h-4 w-4 mr-1" /> Nuovo Costo
            </Button>
          </div>
        </div>

        {/* Generate invoices section */}
        <Card className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-slate-700">Genera fatture per:</span>
            <select value={genMonth} onChange={(e) => setGenMonth(Number(e.target.value))} className="px-3 py-1.5 border rounded-lg text-sm" data-testid="gen-month">
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <input type="number" value={genYear} onChange={(e) => setGenYear(Number(e.target.value))} className="w-20 px-3 py-1.5 border rounded-lg text-sm" data-testid="gen-year" />
            <Button size="sm" onClick={handleGenerate} disabled={generating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="generate-invoices-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
              {generating ? 'Generazione...' : 'Genera Fatture'}
            </Button>
          </div>
        </Card>

        {/* Add/Edit form */}
        {showForm && (
          <Card className="p-4 border-blue-200 bg-blue-50/30">
            <h3 className="font-semibold text-slate-800 mb-3">{editingId ? 'Modifica' : 'Nuovo'} Costo Fisso</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Fornitore *</label>
                <input type="text" value={form.supplier_name} onChange={(e) => setForm(f => ({ ...f, supplier_name: e.target.value }))}
                  placeholder="Es. Enel Energia" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="cost-supplier" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Importo mensile * (EUR)</label>
                <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="150.00" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="cost-amount" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Giorno scadenza</label>
                <input type="number" min="1" max="28" value={form.day_of_month} onChange={(e) => setForm(f => ({ ...f, day_of_month: Number(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="cost-day" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">IBAN</label>
                <input type="text" value={form.iban} onChange={(e) => setForm(f => ({ ...f, iban: e.target.value.toUpperCase() }))}
                  placeholder="IT..." className="w-full px-3 py-2 border rounded-lg text-sm font-mono" data-testid="cost-iban" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Descrizione/Causale</label>
                <input type="text" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Canone mensile..." className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="cost-desc" />
                <p className="text-[11px] text-slate-400 mt-1" data-testid="cost-desc-hint">
                  Alla generazione della fattura verrà aggiunto automaticamente mese e anno: «{(form.description || 'Canone locazione ufficio').trim()} - {MONTHS[new Date().getMonth()]} {new Date().getFullYear()}»
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
                <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="cost-category">
                  <option value="fornitore">Fornitore</option>
                  <option value="partner">Partner</option>
                  <option value="responsabile">Responsabile</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-cost-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                {editingId ? 'Aggiorna' : 'Crea'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }} data-testid="cancel-cost-btn">
                <X className="h-4 w-4 mr-1" /> Annulla
              </Button>
            </div>
          </Card>
        )}

        {/* Costs table */}
        {loading ? (
          <div className="text-center py-12 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Caricamento...</div>
        ) : costs.length === 0 ? (
          <Card className="p-8 text-center text-slate-400"><RefreshCw className="h-8 w-8 mx-auto mb-2" /><p>Nessun costo fisso configurato</p></Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm" data-testid="costs-table">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort('supplier_name')}>
                    <span className="inline-flex items-center">Fornitore<SortIcon col="supplier_name" /></span>
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">IBAN</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort('amount')}>
                    <span className="inline-flex items-center justify-end">Importo<SortIcon col="amount" /></span>
                  </th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort('day_of_month')}>
                    <span className="inline-flex items-center">Giorno<SortIcon col="day_of_month" /></span>
                  </th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Descrizione</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort('active')}>
                    <span className="inline-flex items-center">Stato<SortIcon col="active" /></span>
                  </th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sorted.map((cost) => (
                  <tr key={cost.id} className={`transition-colors ${cost.active ? 'hover:bg-slate-50' : 'bg-slate-50/50 opacity-60'}`} data-testid={`cost-row-${cost.id}`}>
                    <td className="px-4 py-2.5 font-medium">{cost.supplier_name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{cost.iban || <span className="text-amber-500">-</span>}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">{fmtEur(cost.amount)}</td>
                    <td className="px-4 py-2.5 text-center">{cost.day_of_month}</td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs max-w-[200px] truncate" title={cost.description}>{cost.description || '-'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${cost.active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
                        {cost.active ? 'Attivo' : 'Sospeso'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleEdit(cost)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Modifica" data-testid={`edit-cost-${cost.id}`}>
                          <Edit3 className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => handleToggleActive(cost)} className={`p-1.5 rounded ${cost.active ? 'text-slate-400 hover:text-amber-600 hover:bg-amber-50' : 'text-slate-400 hover:text-green-600 hover:bg-green-50'}`}
                          title={cost.active ? 'Sospendi' : 'Riattiva'} data-testid={`toggle-cost-${cost.id}`}>
                          {cost.active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                        </button>
                        <button onClick={() => handleDelete(cost)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="Elimina" data-testid={`delete-cost-${cost.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </Layout>
  );
}
